import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Loader2, ArrowRight, Shuffle, Compass } from "lucide-react";

// One clear, guided "what next" action derived from the customer's real progress.
export function ContinueMyStory() {
  const navigate = useNavigate();
  const [step, setStep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/next-step"); setStep(data); }
    catch { setStep(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const go = async () => {
    if (!step) return;
    switch (step.action) {
      case "first_memory":
      case "collect_more":
        navigate("/answer", { state: { question: step.question } });
        break;
      case "polish":
        navigate(`/memories/${step.memory_id}`, { state: { tab: "memoir" } });
        break;
      case "review_polished":
        navigate(`/memories/${step.memory_id}`, { state: { tab: "memoir" } });
        break;
      case "review_chapter":
        navigate(`/story/${step.chapter_id}`);
        break;
      case "create_chapter":
        setWorking(true);
        try {
          const { data } = await api.post("/chapters/generate", { title: step.title, memory_ids: step.memory_ids });
          toast.success("Your chapter is ready — everything in it came from the memories you shared");
          navigate(`/story/${data.id}`);
        } catch (e) {
          toast.error(e.response?.data?.detail || "We couldn't create that chapter just now. Please try again.");
          setWorking(false);
        }
        break;
      case "read_memoir":
        navigate("/book", { state: { read: true } });
        break;
      case "finish":
        navigate("/book");
        break;
      default:
        navigate("/answer");
    }
  };

  if (loading) {
    return (
      <div className="rounded-3xl bg-teal/95 text-cream p-6 flex items-center gap-3 animate-fade-up">
        <Loader2 className="animate-spin" size={20} /> <span className="text-cream/80">Working out your next step…</span>
      </div>
    );
  }
  if (!step) return null;

  const showAskAnother = step.action === "first_memory" || step.action === "collect_more";

  return (
    <div data-testid="continue-my-story" className="rounded-3xl bg-teal text-cream p-6 sm:p-7 relative overflow-hidden animate-fade-up">
      <div className="absolute -right-12 -top-12 w-52 h-52 rounded-full bg-sage/20 blur-3xl" />
      <p className="text-xs uppercase tracking-widest text-cream/60 font-semibold relative">Your next step</p>
      <p data-testid="next-step-message" className="font-serif text-xl sm:text-2xl leading-snug mt-2 relative max-w-2xl">{step.message}</p>
      <div className="mt-5 flex flex-wrap items-center gap-3 relative">
        <button data-testid="continue-my-story-btn" onClick={go} disabled={working}
          className="inline-flex items-center justify-center gap-2 bg-coral hover:bg-coraldark text-white px-6 h-12 rounded-full font-semibold transition-colors disabled:opacity-60">
          {working ? <><Loader2 className="animate-spin" size={18} /> Creating your chapter…</> : <>Continue my story <ArrowRight size={18} /></>}
        </button>
        {showAskAnother && (
          <button data-testid="ask-something-else-btn" onClick={() => navigate("/remember")}
            className="inline-flex items-center gap-2 text-cream/85 hover:text-cream text-sm font-medium transition-colors">
            <Shuffle size={16} /> Ask me something else
          </button>
        )}
        <button data-testid="continue-browse-btn" onClick={() => navigate("/remember")}
          className="inline-flex items-center gap-2 text-cream/70 hover:text-cream text-sm transition-colors ml-auto">
          <Compass size={15} /> Browse questions
        </button>
      </div>
    </div>
  );
}
